<template>
    <div class="contact-journeys-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Manage Contact's Journeys</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="loading"/>
                <div class="widget-wrapper" v-else>
                    <div class="journeys-container active">
                        <div class="title">Active Journeys</div>
                        <div class="description">
                            <span v-if="activeJourneys.length > 0">Clicking the X icon will remove this contact from the journey.</span>
                            <span v-else>This contact is not on any active journeys.</span>
                        </div>
                        <div>
                            <div v-for="journey in activeJourneys" :key="journey.id">
                                <div class="journey">
                                    [{{ `Step ${ journey.actions.length }` }}] {{ `${ journey.name }` }}
                                    <div class="btn-container">
                                        <app-button class="view-button" @click="viewJourneySteps(journey.id)">
                                            <span class="svg-container">
                                                <q5-email-widget-svg />
                                            </span>
                                        </app-button>
                                        <app-button class="remove-button" @click="confirmRemoveFromJourney(journey)">
                                            <span class="svg-container">
                                                <s3-close-svg />
                                            </span>
                                        </app-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="journeys-container new-journeys">
                        <div class="title">Start a New Journey</div>
                        <div class="description">
                            <span v-if="newJourneys.length > 0">Clicking the + sign will automatically start this contact on the selected journey.</span>
                            <span v-else>You have no active journeys.</span>
                        </div>
                        <app-button class="add-journey-btn" v-if="newJourneys.length === 0" @click="openJourneyPage()">Create a Journey</app-button>
                        <div v-for="journey in newJourneys" :key="journey.id">
                            <div class="journey">
                                {{ `${ journey.name }` }}
                                <div class="btn-container">
                                    <app-button class="view-button" @click="viewJourney(journey.id)">
                                        <span class="svg-container">
                                            <q5-email-widget-svg />
                                        </span>
                                    </app-button>
                                    <app-button class="add-button" @click="confirmAddToJourney(journey)">
                                        <span class="svg-container">
                                            <o6-add-svg />
                                        </span>
                                    </app-button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="journeys-container stopped-journeys" v-if="stoppedJourneys.length > 0">
                        <div class="title">Stopped Journeys</div>
                        <div class="description">
                            <span>These journeys have been stopped either by a user or through the contact unsubscribing. Clicking the + sign will automatically place them where they left off.</span>
                        </div>
                        <div v-for="journey in stoppedJourneys" :key="journey.id">
                            <div class="journey">
                                {{ `${ journey.name }` }}
                                <div class="btn-container">
                                    <app-button class="view-button" @click="viewJourneySteps(journey.id)">
                                        <span class="svg-container">
                                            <q5-email-widget-svg />
                                        </span>
                                    </app-button>
                                    <app-button class="add-button" v-if="journey.unsubscribed">
                                        <span class="svg-container">
                                            <i4-unsubscribe-svg />
                                        </span>
                                    </app-button>
                                    <app-button class="add-button" @click="confirmAddToJourney(journey)" v-else>
                                        <span class="svg-container">
                                            <o6-add-svg />
                                        </span>
                                    </app-button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="journeys-container completed-journeys">
                        <div class="title">Completed Journeys</div>
                        <div class="description">
                            <span v-if="completedJourneys.length > 0">These journeys have been completed by this contact.</span>
                            <span v-else>This contact has no completed journeys.</span>
                        </div>
                        <div class="journeys" v-for="journey in completedJourneys" :key="journey.id">
                            <div class="journey">
                                {{ `${ journey.name }` }}
                                <div class="btn-container">
                                    <app-button class="view-button" @click="viewJourneySteps(journey.id)">
                                        <span class="svg-container">
                                            <q5-email-widget-svg />
                                        </span>
                                    </app-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="closeWidget">
                        Close
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const Q5EmailOpenSvg = () => import('@/components/svg/grid/q5-email-open-svg');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const LoadingScreenSvg = () => import('../svg/loading-screen-svg');
const I4UnsubscribeSvg = () => import('@/components/svg/grid/i4-unsubscribe-svg');

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import log, { LogLevel } from "@/scripts/logger";

export default {
    name: 'contact-journeys-widget',

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'q5-email-widget-svg': Q5EmailOpenSvg,
        'o6-add-svg': O6AddSvg,
        's3-close-svg': S3CloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'i4-unsubscribe-svg': I4UnsubscribeSvg
    },

    data() {
        return {
            loading: true,
            journeys: [],
            clientId: null,
            contact: {
                id: this.widget.contactId,
                name: null,
            }
        }
    },

    computed: {
        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        activeJourneys() {
            return this.journeys.filter(journey => journey.active && journey.actions.some(a => !a.occurred));
        },

        newJourneys() {
            return this.journeys.filter(journey => journey.active && journey.actions.length === 0);
        },

        stoppedJourneys() {
            return this.journeys
                .filter(journey => {
                    var notOnJourney = journey.actions.length === 0;
                    var isActivelyOnJourney = journey.actions.some(a => !a.occurred);

                    if (notOnJourney || isActivelyOnJourney) {
                        return false;
                    }
                    else {
                        var lastAction = journey.actions[journey.actions.length - 1];

                        var step = journey.steps.find(s => s.id === lastAction.stepId);

                        var isConditionStep = step.typeId === 3;
                        var hasChildren = step.trueChildStepId;
                        var isStopped = !isConditionStep && hasChildren;

                        return isStopped;
                    }
                });
        },

        completedJourneys() {
            return this.journeys
                .filter(journey => {
                    var notOnJourney = journey.actions.length === 0;
                    var isActivelyOnJourney = journey.actions.some(a => !a.occurred);

                    if (notOnJourney || isActivelyOnJourney) {
                        return false;
                    }
                    else {
                        var lastAction = journey.actions[journey.actions.length - 1];

                        var step = journey.steps.find(s => s.id === lastAction.stepId);

                        var isConditionStep = step.typeId === 3;
                        var hasNoChildren = !step.trueChildStepId && !step.falseChildStepId;
                        var isComplete = isConditionStep || hasNoChildren;

                        return isComplete;
                    }
                });
        }
    },

    methods: {
        viewJourneySteps(journeyId) {
            EventBus.$emit('open-widget', {
                name: 'contact-journey-steps-widget',
                contactId: this.contact.id,
                journeyId: journeyId,
            });
        },

        viewJourney(journeyId) {
            window.open(`/Journeys/${ journeyId }/Design`, '_blank');
        },

        openJourneyPage() {
            this.closeWidget();
            this.$router.push(`${this.routeBaseUrl}/Journeys`);
        },

        getContact() {
            this.loading = true;

            return new Promise((resolve, reject) => {
                var select = `$select=Id,ClientId,FirstName,LastName`;
                var restUrl = `/service/Contacts?$filter=Id eq ${ this.contact.id }&${ select }`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.contact.name = this.$helpers.getName(response.data.value[0].FirstName, response.data.value[0].LastName);
                        this.clientId = response.data.value[0].ClientId;

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(`Failed to get contact`, { error });
                        reject();
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            })
        },

        getJourneys() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=ClientId eq ${ this.clientId }`;
                var select = `$select=Id,Name,IsActive`;
                var expand = `$expand=JourneySteps($select=Id,JourneyStepTypeId,TrueChildStepId,FalseChildStepId),JourneyActions($filter=ContactId eq ${ this.contact.id };$select=Id,JourneyStepId,ActionDate,Occurred;$expand=Message($select=Unsubscribed);$orderby=ActionDate)`;
                var orderBy = `$orderby=Name`;

                var restUrl = `/service/Journeys?${ filter }&${ select }&${ expand }&${ orderBy }`;
    
                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.journeys = response.data.value
                            .map(journey => {
                                var actions = journey.JourneyActions.map(this.mapAction);
                                var unsubscribed = actions.length > 0 && actions.some(a => a.unsubscribed);

                                return {
                                    id: journey.Id,
                                    name: journey.Name,
                                    active: journey.IsActive,
                                    actions: actions,
                                    steps: journey.JourneySteps.map(this.mapStep),
                                    unsubscribed: unsubscribed
                                };
                            });

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('An error occurred while trying to get your journeys', { error });
                        reject();
                    });
            });
        },

        mapStep(step) {
            return {
                id: step.Id,
                typeId: step.JourneyStepTypeId,
                trueChildStepId: step.TrueChildStepId,
                falseChildStepId: step.FalseChildStepId
            };
        },

        mapAction(action) {
            return {
                id: action.Id,
                stepId: action.JourneyStepId,
                actionDate: this.$moment.utc(action.ActionDate).local(),
                occurred: action.Occurred,
                unsubscribed: action.Message ? action.Message.Unsubscribed : false
            };
        },

        confirmAddToJourney(journey) {
            this.actions = {
                confirm: {
                    label: `Confirm`,
                    action: () => { this.add(journey) }
                },
                cancel: {
                    label: 'Cancel'
                }
            };

            this.$modal.show(AppPopup, {
                type: 'confirm-popup',
                title: `Start Journey Confirmation`,
                content: `You're about to enroll ${ this.contact.name } on the journey <strong>"${ journey.name }"</strong>. Their journey will immediately begin.`,
                actions: this.actions,
                confirmText: 'Confirm'
            },{
                height: this.$helpers.setPopupHeight(),
                width: this.$helpers.setPopupWidth()
            });
        },

        confirmRemoveFromJourney(journey) {
            this.actions = {
                confirm: {
                    label: `Confirm`,
                    action: () => { this.delete(journey) }
                },
                cancel: {
                    label: 'Cancel'
                }
            };

            this.$modal.show(AppPopup, {
                type: 'alert-popup',
                title: `Journey Removal Confirmation`,
                content: `You're about to remove ${ this.contact.name } from the journey <strong>"${ journey.name }"</strong>. Once removed this contact will not be able to receive any of the follow up campaigns from this journey. Are you sure you want to remove it?`,
                actions: this.actions,
                confirmText: 'Confirm'
            },{
                height: this.$helpers.setPopupHeight(),
                width: this.$helpers.setPopupWidth()
            });
        },

        add(journey) {
            this.loading = true;

            var restUrl = `/api/Journeys/${ journey.id }/Contacts/${ this.contact.id }/Start`;

            this.$api
                .post(restUrl)
                .then(() => {
                    EventBus.$emit('contact-journeys:update');
                    this.getJourneys();
                    this.$helpers.showSuccessNotification(`${ this.contact.name } has been successfully added to ${ journey.name }.`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to add ${ this.contact.name } to ${ journey.name }.`, { level: LogLevel.DEBUG });
                    log.error(`Failed to add a contact to a journey.`, error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        delete(journey) {
            this.loading = true;

            var restUrl = `/api/Journeys/${ journey.id }/Contacts/${ this.contact.id }/Stop`;

            this.$api
                .delete(restUrl)
                .then(() => {
                    EventBus.$emit('contact-journeys:update');
                    this.getJourneys();
                    this.$helpers.showSuccessNotification(`${ this.contact.name } has been successfully removed from ${ journey.name }.`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to remove ${ this.contact.name } from ${ journey.name }.`, { level: LogLevel.DEBUG });
                    log.error(`Failed to remove a contact from a journey.`, error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        }
    },

    created() {
        this.getContact()
            .then(this.getJourneys);
    }
}
</script>

<style>
.contact-journeys-widget .content-wrapper .journeys-container {
    background: #FFFFFF 0% 0% no-repeat padding-box;
    padding: 45px 50px;
    border: 2px solid #E9E9EF;
    border-radius: 22px;
    margin-bottom: 30px;
    position: relative;
}

.contact-journeys-widget .content-wrapper .journeys-container .title {
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    padding: 0 45px;
    color: #382153;
    background-color: #fff;
    position: absolute;
    top: -15px;
    left: 50%;
    margin-left: -107px;
}

.contact-journeys-widget .content-wrapper .journeys-container.completed-journeys .title {
    margin-left: -129px;
}

.contact-journeys-widget .content-wrapper .journeys-container .description {
    margin-bottom: 15px;
}

.contact-journeys-widget .content-wrapper .journeys-container .app-button {
    position: relative;
    padding: 0px 10px;
    background: transparent;
}

.contact-journeys-widget .content-wrapper .journeys-container .app-button .svg-container {
    display: flex;
    align-items: center;
    justify-content: center;
}

.contact-journeys-widget .content-wrapper .journeys-container .app-button.add-button {
    padding: 0px 20px;
}

.contact-journeys-widget .content-wrapper .journeys-container .journey .app-button:hover {
    background-color: rgba(0,0,0,0.25);
}

.contact-journeys-widget .content-wrapper .journeys-container .app-button.add-button .svg-container {
    position: absolute;
    right: 10px;
}

.contact-journeys-widget .content-wrapper .journeys-container .app-button.remove-button {
    padding: 0 10px;
}

.contact-journeys-widget .content-wrapper .journeys-container .journey,
.contact-journeys-widget .content-wrapper .journeys-container .journey .btn-container {
    display: flex;
    align-items: center;
}

.contact-journeys-widget .content-wrapper .journeys-container .journey .btn-container {
    margin-right: -15px;
}

.contact-journeys-widget .journey .btn-container .app-button .svg-container {
    min-width: 20px;
    max-width: 20px;
}

.contact-journeys-widget .journey .btn-container .app-button .q5-email-open-svg .a,
.contact-journeys-widget .journey .btn-container .app-button .o6-add-svg .a {
    fill: #fff;
}

.contact-journeys-widget .journey .btn-container .app-button .s3-close-svg {
    width: 14px;
    height: auto;
}

.contact-journeys-widget .journey .btn-container .app-button .s3-close-svg .a {
    stroke: #fff;
}

.contact-journeys-widget .journey .i4-unsubscribe-svg path {
    fill: #FFFFFF;
}

.contact-journeys-widget .content-wrapper .journeys-container .journey {
    justify-content: space-between;
    border-radius: 22px;
    padding: 0px 15px;
    margin: 0 auto;
    font: normal normal normal 16px/11px Roboto;
    letter-spacing: 0px;
    color: #FFFFFF;
    margin-bottom: 10px;
    background: #877D88;
}

.contact-journeys-widget .content-wrapper .journeys-container.active .journey {
    background: #18AEFF;
}

.contact-journeys-widget .content-wrapper .journeys-container.stopped-journeys .journey {
    background: #F30070;
}

.contact-journeys-widget .content-wrapper .journeys-container.completed-journeys .journey {
    background-color: #2ada2b;
}

.contact-journeys-widget .content-wrapper .journeys-container.new-journeys .add-journey-btn {
    padding: 0 35px;
    margin: 0 auto;
    background: #18AEFF;
}

.contact-journeys-widget .widget-footer .btn.transparent-btn {
    background-color: transparent;
    color: #877D88;
    transition: background-color 250ms ease-in;   
}

.contact-journeys-widget .widget-footer .btn.transparent-btn:hover {
    background: #E1E0E5;
}
</style>